import Api from '../api';

// Toda escrita responde o bloco inteiro, já com o progresso e a etapa completa recalculados.
export type ProposalRequirementMomento = 'antes_do_lance' | 'apos_lance';
export type ProposalRequirementCategoria = 'proposta' | 'habilitacao';
export type ProposalRequirementStatus =
  | 'pendente'
  | 'aguardando_revisao'
  | 'confirmado'
  | 'ignorada';
export type ProposalDocumentTipo = 'arquivo' | 'amostra' | 'garantia';
export type ProposalRequirementOrigem = 'edital' | 'manual';

export interface ProposalRequirementFileResponse {
  key: string;
  nome: string;
  enviado_por: number | null;
  enviado_por_nome: string | null;
  enviado_em: string | null;
  url?: string;
}

interface ProposalRequirementBaseResponse {
  id: string;
  origem: ProposalRequirementOrigem;
  nome: string;
  momento: ProposalRequirementMomento;
  categoria: ProposalRequirementCategoria;
  status: ProposalRequirementStatus;
  ignorada: boolean;
  ignorada_por: number | null;
  ignorada_por_nome: string | null;
  ignorada_em: string | null;
  arquivos: ProposalRequirementFileResponse[];
  trecho: string | null;
}

export type ProposalDeclarationFieldSituacao = 'sugerido' | 'aceito' | 'recusado' | 'pendente';
export type ProposalDeclarationTextoOrigem = 'edital' | 'livre' | 'modelo';

// rotulo e valor_sugerido vêm do contrato e da empresa a cada leitura; o PATCH grava só
// id, valor e situacao.
export interface ProposalDeclarationFieldResponse {
  id: string;
  valor: string | null;
  situacao: ProposalDeclarationFieldSituacao;
  rotulo?: string | null;
  valor_sugerido?: string | null;
}

export interface ProposalDeclarationResponse extends ProposalRequirementBaseResponse {
  campos?: ProposalDeclarationFieldResponse[];
  // HTML restrito (p, br, strong, em, u, ul, ol, li, span[data-campo], text-align), sanitizado no back.
  texto_final?: string | null;
  texto_origem?: ProposalDeclarationTextoOrigem | null;
  modelo_id?: string | null;
  pede_comprovacao?: boolean | null;
  // Assinalada no sistema da licitação: sem texto nem arquivo, o cliente só confirma.
  assinalar_no_portal?: boolean | null;
  comprovacao?: string | null;
  modelo_sugerido?: string | null;
  no_modelo?: boolean | null;
  confirmada_por: number | null;
  confirmada_por_nome: string | null;
  confirmada_em: string | null;
}

export type ProposalGuaranteeEstado =
  | 'apolice_importada'
  | 'cotacao_concluida'
  | 'documento_enviado'
  | 'sem_apolice';
export type ProposalGuaranteeModalidade = 'caucao' | 'fianca' | 'seguro';

// link_pdf aponta para /conlicitaseg/insurance_quotes/download_file?id=…&type=…
export interface ProposalGuaranteeResponse {
  estado: ProposalGuaranteeEstado;
  valor_exigido: string | null;
  forma: 'conlicitaseg' | 'documento' | null;
  modalidade: ProposalGuaranteeModalidade | null;
  apolice: {
    numero: string | null;
    seguradora: string | null;
    premio: number | null;
    valor_segurado: number | null;
    vigencia_inicio: string | null;
    vigencia_fim: string | null;
    link_pdf: string | null;
  } | null;
  cotacao: {
    seguradora: string | null;
    premio: number | null;
    status: string | null;
    link_pdf: string | null;
    insurance_process_id: number | null;
  } | null;
  aviso: string | null;
  insurance_quote_id: number | null;
}

export interface ProposalDocumentResponse extends ProposalRequirementBaseResponse {
  tipo: ProposalDocumentTipo;
  garantia?: ProposalGuaranteeResponse | null;
  confirmado_por: number | null;
  confirmado_por_nome: string | null;
  confirmado_em: string | null;
}

export interface ProposalDocumentsResponse {
  declaracoes: ProposalDeclarationResponse[];
  documentos: ProposalDocumentResponse[];
  progresso: { confirmadas: number; total: number };
  etapa_declaracoes_completa: boolean;
}

// Os erros desta etapa vêm em `erro` (422/404), resolvidos no .then pelo interceptor global.
export interface ProposalDocumentsErrorResponse {
  erro: string;
}

export type ProposalRequirementKindResponse = 'declaracoes' | 'documentos';

export interface ProposalRequirementPatch {
  confirmar?: boolean;
  reabrir?: boolean;
  ignorada?: boolean;
  campos?: Pick<ProposalDeclarationFieldResponse, 'id' | 'valor' | 'situacao'>[];
  texto_final?: string;
  texto_origem?: ProposalDeclarationTextoOrigem;
  modelo_id?: string;
}

export interface ProposalManualRequirementPayload {
  tipo_exigencia: 'declaracao' | 'documento';
  nome: string;
  momento: ProposalRequirementMomento;
  tipo?: 'arquivo' | 'amostra';
}

type Result = ProposalDocumentsResponse | ProposalDocumentsErrorResponse;

const basePath = (biddingId: number) => `/gestao_ia/licitacoes/${biddingId}/proposta`;

export const getProposalDocuments = (biddingId: number) =>
  Api.get<ProposalDocumentsResponse>(`${basePath(biddingId)}/declaracoes`);

export const updateProposalRequirement = (
  biddingId: number,
  kind: ProposalRequirementKindResponse,
  id: string,
  payload: ProposalRequirementPatch,
) => Api.patch<Result>(`${basePath(biddingId)}/${kind}/${encodeURIComponent(id)}`, payload);

export const uploadProposalRequirementFile = (
  biddingId: number,
  kind: ProposalRequirementKindResponse,
  id: string,
  file: File,
  modalidade?: ProposalGuaranteeModalidade,
) => {
  const formData = new FormData();
  formData.append('arquivo', file, file.name);
  if (modalidade) formData.append('modalidade', modalidade);
  return Api.post<Result>(
    `${basePath(biddingId)}/${kind}/${encodeURIComponent(id)}/arquivos`,
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
};

// A key do S3 tem "/" e vai na query string; o back confere se ela é desta exigência.
export const deleteProposalRequirementFile = (
  biddingId: number,
  kind: ProposalRequirementKindResponse,
  id: string,
  arquivoKey: string,
) =>
  Api.delete<Result>(`${basePath(biddingId)}/${kind}/${encodeURIComponent(id)}/arquivos`, {
    params: { arquivo_key: arquivoKey },
  });

export const createProposalManualRequirement = (
  biddingId: number,
  payload: ProposalManualRequirementPayload,
) => Api.post<Result>(`${basePath(biddingId)}/exigencias`, payload);
